import * as React from 'react';
import { Spinner } from 'sos-common/js/components/Spinner/Spinner';
import { SelectBox, SelectBoxState } from 'sos-common/js/components/FormControls/SelectBox';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import { makeTranslator } from 'sos-common/js/utils/translations';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import AttentionBox from 'sos-common/js/components/AttentionBox/Attentionbox';
import { getOccupationTexts } from '../../services/epi.texts.service';
import { IPreAnsokanProps, IPreAnsokanState, Section, ALL_SECTIONS } from './PreAnsokan.types';

const YrkesId = 'yrkesId';
let Translate: (key: string, defaultValue?: string) => string;

export class PreAnsokan extends React.Component<IPreAnsokanProps, IPreAnsokanState> {
  private _spinnerRef: React.RefObject<HTMLDivElement>;
  private _whereEducationRef: React.RefObject<HTMLHeadingElement>;
  private _protectedOccupationTitleHeading: string;
  private _protectedOccupationTitleBody: string;

  constructor(props: IPreAnsokanProps) {
    super(props);
    this._spinnerRef = React.createRef();
    Translate = makeTranslator(props.translations);
    this.state = {
      visibilityMap: this.getDefaultVisibilityMap(),
      activeOccupation: '',
      occupationModel: null,
      isLoading: false,
      showSpinner: false,
    };

    this._whereEducationRef = React.createRef();
  }

  getDefaultVisibilityMap = (): Map<Section, boolean> => {
    return new Map<Section, boolean>(
      ALL_SECTIONS.map((section) => {
        return [section, section === Section.NoOccupationInList];
      }),
    );
  };

  removeSpinner = () => {
    this._spinnerRef.current.classList.add('die');
    setTimeout(() => {
      this.setState({
        showSpinner: false,
      });
    }, 500);
  };

  changeOccupation = (occupation: SelectBoxState) => {
    // Reset to default if no occupation
    if (!occupation.value) {
      window.history.replaceState('', '', `?${YrkesId}=`);
      return this.setState({ visibilityMap: this.getDefaultVisibilityMap() });
    }

    window.history.replaceState('', '', `?${YrkesId}=${occupation.value}`);
    const visibilityMap = new Map(this.getDefaultVisibilityMap());
    visibilityMap.set(Section.NoOccupationInList, false);

    this.setState({ visibilityMap, isLoading: true, showSpinner: true }, () => {
      // Get data
      getOccupationTexts(occupation.value, this.props.pageLanguage).then((occupationModel) => {
        if (occupationModel.IsProtectedOccupationTitle) {
          this._protectedOccupationTitleBody = occupationModel.ProtectedOccupation.ProtectedOccupationTitleInfoBody;
          this._protectedOccupationTitleHeading =
            occupationModel.ProtectedOccupation.ProtectedOccupationTitleInfoHeading;
          visibilityMap.set(Section.ProtectedOccupationTitle, true);
        } else visibilityMap.set(Section.WhereEducationChoice, true);

        this.setState(
          {
            visibilityMap,
            activeOccupation: occupation.selectedText,
            occupationModel,
            isLoading: false,
          },
          () => {
            this.removeSpinner();
            this._whereEducationRef.current?.focus();
          },
        );
      });
    });
  };

  updateVisibilityMap = (chosenSection: Section, sections: Section[]) => {
    const visibilityMap = new Map(this.state.visibilityMap);

    sections.forEach((section) => {
      visibilityMap.set(section, false);
    });

    visibilityMap.set(chosenSection, true);
    this.setState({ visibilityMap });
  };

  changeWhereEducation = (section: FormField<Section>) => {
    this.updateVisibilityMap(section.value, [
      Section.EducationInSwe,
      Section.EducationInEu,
      Section.EducationInWorld,
      Section.HaveWorkedThreeYears,
      Section.HaveNotWorkedThreeYears,
    ]);
  };

  private hasActiveAnsokan = () => {
    return this.props.activeIds.includes(this.state.occupationModel.OccupationId);
  };

  public render() {
    const {
      occupationList,
      occupationDefaultOptionText,
      whereEducation,
      applyForLeg,
      noEducationHeading,
      noEducationBody,
      workedThreeYears,
    } = this.props;
    const { visibilityMap, activeOccupation, occupationModel, isLoading, showSpinner } = this.state;

    const currentHeading = (
      <h2>
        {applyForLeg} {activeOccupation.toLowerCase()}
      </h2>
    );

    const hasActiveAnsokan = this.props.warning.Title && this.props.warning.Text && (
      <AttentionBox
        cssClass="pre-ansokan__warning-text attentionbox--brown"
        icon="Infocirkel"
        heading={this.props.warning.Title}
        bodytext={this.props.warning.Text}
      />
    );

    return (
      <div className={`pre-ansokan m-b-4`}>
        <div className="pre-ansokan__occupationwrap m-b-2">
          <SelectBox
            additionalAttributes={{ 'aria-labelledby': 'pickYrke' }}
            className="max25 pre-ansokan__occupation"
            defaultOptionText={occupationDefaultOptionText}
            options={occupationList}
            onChange={this.changeOccupation}
            disabled={isLoading}
            fieldName="pickYrke"
          />
          {showSpinner && (
            <div ref={this._spinnerRef} className="pre-ansokan__spinner">
              <Spinner />
            </div>
          )}
        </div>
        <div role="log" aria-live="polite">
          {visibilityMap.get(Section.NoOccupationInList) && (
            <AttentionBox
              heading={noEducationHeading}
              bodytext={noEducationBody}
              cssClass="attentionbox--brown attentionbox--sharp"
              icon="Infocirkel"
            />
          )}
          {visibilityMap.get(Section.ProtectedOccupationTitle) && (
            <AttentionBox
              heading={this._protectedOccupationTitleHeading}
              bodytext={this._protectedOccupationTitleBody}
              cssClass="attentionbox--brown attentionbox--sharp"
              icon="Infocirkel"
            />
          )}
        </div>
        <div role="log" aria-live="polite">
          {visibilityMap.get(Section.WhereEducationChoice) && (
            <div className="m-b-2">
              <h2 ref={this._whereEducationRef}>{whereEducation}</h2>
              <RadioButtonGroup fieldName="pickWhereEdu" onChange={this.changeWhereEducation}>
                <RadioButton value={Section.EducationInSwe} label={Translate('preAnsokan/inSweden')} />
                <RadioButton value={Section.EducationInEu} label={Translate('preAnsokan/inEu')} />
                <RadioButton value={Section.HaveNotWorkedThreeYears} label={Translate('preAnsokan/outEu')} />
                <RadioButton value={Section.HaveWorkedThreeYears} label={`${Translate('preAnsokan/outEuWorkedIn')}`} />
              </RadioButtonGroup>
            </div>
          )}
        </div>

        <div role="log" aria-live="polite">
          {visibilityMap.get(Section.EducationInSwe) && (
            <>
              {currentHeading}
              {this.hasActiveAnsokan() && hasActiveAnsokan}

              {occupationModel.EducationInSweden && (
                <>
                  {occupationModel.EducationInSweden.PricingForYrke && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EducationInSweden.PricingForYrke)} />
                  )}
                  {occupationModel.EducationInSweden.TimeLimit && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EducationInSweden.TimeLimit)} />
                  )}
                  {occupationModel.EducationInSweden.LinkForInfoPage && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EducationInSweden.LinkForInfoPage)} />
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Visible always if Education in EU chosen */}
        <div role="log" aria-live="polite">
          {visibilityMap.get(Section.EducationInEu) && occupationModel.Eu.ImportantInfo && (
            <AttentionBox
              icon="Infocirkel_normal20px20px"
              cssClass="attentionbox--brown m-b-1"
              heading={occupationModel.Eu.ImportantInfo.Title}
              bodytext={occupationModel.Eu.ImportantInfo.Text}
            />
          )}
        </div>
        <div role="log" aria-live="polite">
          {(visibilityMap.get(Section.HaveNotWorkedThreeYears) || visibilityMap.get(Section.HaveWorkedThreeYears)) &&
            occupationModel.EjEuGeneralInformation.ImportantInfo && (
              <AttentionBox
                icon="Infocirkel_normal20px20px"
                cssClass="attentionbox--brown m-b-1"
                heading={occupationModel.EjEuGeneralInformation.ImportantInfo.Title}
                bodytext={occupationModel.EjEuGeneralInformation.ImportantInfo.Text}
              />
            )}
        </div>
        <div role="log" aria-live="polite">
          {visibilityMap.get(Section.EducationInEu) && (
            <>
              {currentHeading}
              {occupationModel.Eu && (
                <>
                  {occupationModel.Eu.PricingForYrke && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.Eu.PricingForYrke)} />
                  )}
                  {occupationModel.Eu.TimeLimit && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.Eu.TimeLimit)} />
                  )}
                  {occupationModel.Eu.LinkForInfoPage && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.Eu.LinkForInfoPage)} />
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div role="log" aria-live="polite">
          {visibilityMap.get(Section.HaveWorkedThreeYears) && (
            <>
              {currentHeading}
              {occupationModel.EjEuMoreThan3Years && (
                <>
                  {occupationModel.EjEuMoreThan3Years.PricingForYrke && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EjEuMoreThan3Years.PricingForYrke)} />
                  )}
                  {occupationModel.EjEuMoreThan3Years.TimeLimit && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EjEuMoreThan3Years.TimeLimit)} />
                  )}
                  {occupationModel.EjEuMoreThan3Years.LinkForInfoPage && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EjEuMoreThan3Years.LinkForInfoPage)} />
                  )}
                </>
              )}
            </>
          )}
          {visibilityMap.get(Section.HaveNotWorkedThreeYears) && (
            <>
              {currentHeading}
              {occupationModel.EjEuLessThan3Years && (
                <>
                  {occupationModel.EjEuLessThan3Years.PricingForYrke && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EjEuLessThan3Years.PricingForYrke)} />
                  )}
                  {occupationModel.EjEuLessThan3Years.TimeLimit && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EjEuLessThan3Years.TimeLimit)} />
                  )}
                  {occupationModel.EjEuLessThan3Years.LinkForInfoPage && (
                    <div dangerouslySetInnerHTML={setInnerHtml(occupationModel.EjEuLessThan3Years.LinkForInfoPage)} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}
